import * as styles from "./List.css";

import { clsx } from "clsx";
import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { JSX } from "react";

import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";

import { ListContext } from "./List.context";
import type { ListContextValue } from "./List.context";
import type { ListProps, ListVirtualizer } from "./List.types";
import { useListNavigation } from "./useListNavigation";

export function List({
  size = "md",
  separators = false,
  fade = false,
  selectedValue = null,
  onSelectionChange,
  scrollbarAlwaysVisible = false,
  scrollbarExpandOnHover = true,
  viewportRef: forwardedViewportRef,
  label,
  className,
  children,
  ...props
}: ListProps): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const virtualizerRef = useRef<ListVirtualizer | null>(null);

  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );

  const attachViewport = useCallback((node: HTMLDivElement | null) => {
    viewportRef.current = node;
    setScrollElement(node);
  }, []);

  useImperativeHandle(
    forwardedViewportRef,
    () => viewportRef.current as HTMLDivElement,
  );

  const handleKeyDown = useListNavigation({ viewportRef, virtualizerRef });

  const selectable = undefined !== onSelectionChange;

  const context = useMemo<ListContextValue>(
    () => ({
      size,
      separators,
      fade,
      selectable,
      selectedValue,
      selectValue: (value: string) => onSelectionChange?.(value),
      viewportRef,
      scrollElement,
      virtualizerRef,
    }),
    [
      size,
      separators,
      fade,
      selectable,
      selectedValue,
      onSelectionChange,
      scrollElement,
    ],
  );

  return (
    <ListContext.Provider value={context}>
      <ScrollArea className={clsx(styles.root, className)} {...props}>
        <ScrollAreaViewport
          ref={attachViewport}
          fade={fade}
          onKeyDown={handleKeyDown}
        >
          {/* Base UI sets min-width: fit-content inline, which would let
              the longest item widen the list past its viewport */}
          <ScrollAreaContent style={{ minWidth: 0 }}>
            <div
              data-slot="list"
              role={selectable ? "listbox" : undefined}
              aria-label={label}
              tabIndex={selectedValue ? -1 : 0}
              className={styles.list({ size })}
            >
              {children}
            </div>
          </ScrollAreaContent>
        </ScrollAreaViewport>

        <ScrollAreaScrollbar
          orientation="vertical"
          alwaysVisible={scrollbarAlwaysVisible}
          expandOnHover={scrollbarExpandOnHover}
        >
          <ScrollAreaThumb />
        </ScrollAreaScrollbar>
      </ScrollArea>
    </ListContext.Provider>
  );
}
